import React, { useEffect, useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { useFormContext } from "react-hook-form";

import {
	HookFormCheckbox,
	HookFormInputGroup,
	HookFormRadioButton,
	HookSelectFormGroup,
} from "components";
import { CreatedViaTsysApiWarning, DebounceTimeout } from "globalConstants";
import { useBackendPlatform } from "hooks";
import {
	backendPlatformsWithFrontendPlatforms,
	getDefaultValuesForBackendPlatforms,
} from "mappers";
import {
	AchProcessorType,
	BackEndPlatformTypes,
	FrontEndPlatformTypes,
	TerminalFileBuildOptionTypes,
	TerminalProgramTypes,
} from "types/enums";
import { alphaNumericNormalizer, trimNormalizer } from "validators";
import { maxLength50, required } from "validators/validation";

import styles from "../terminalSolution.module.scss";

type ProgramProps = {
	processorId?: number;
	achProcessor?: AchProcessorType;
	isLoading?: boolean;
	isTidVNumberFromApi?: boolean;
	isHiddenForApplication?: boolean;
	sectionName?: string;
};

const Program = ({
	processorId,
	achProcessor,
	isLoading = false,
	isTidVNumberFromApi = false,
	isHiddenForApplication = false,
	sectionName = "",
}: ProgramProps) => {
	const {
		register,
		watch,
		setValue,
		formState: { errors },
	} = useFormContext();

	const defaultBackEndPlatformValues =
		getDefaultValuesForBackendPlatforms(processorId);

	const backEndPlatformWatched = watch(`${sectionName}backEndPlatform`);
	const {
		isBackEndPlatformOmaha,
		isBackEndPlatformNorth,
		isBackEndPlatformTsys,
		isBackEndPlatformOther,
	} = useBackendPlatform(defaultBackEndPlatformValues?.backEndPlatform);

	const { isBackEndPlatformOther: isWatchedBackEndPlatformOther } =
		useBackendPlatform(backEndPlatformWatched);

	const id = watch("id");
	const isFileBuildOnly = watch("isFileBuildOnly");
	const gatewayName = watch("gateway.gatewayName");

	useEffect(() => {
		if (isFileBuildOnly) {
			setValue(
				`${sectionName}backEndPlatform`,
				defaultBackEndPlatformValues?.backEndPlatform,
			);
		}
	}, [
		defaultBackEndPlatformValues?.backEndPlatform,
		isFileBuildOnly,
		sectionName,
		setValue,
	]);

	const otherFrontEndPlatform = watch(`${sectionName}otherFrontEndPlatform`);

	const programTypes = useMemo(() => {
		return id
			? TerminalProgramTypes.getOptions()
			: [TerminalProgramTypes.Normal];
	}, [id]);

	const displayFrontEndPlatform =
		backendPlatformsWithFrontendPlatforms.includes(backEndPlatformWatched);

	const onBackEndStraddlePlatformChange = () => {
		setValue(
			"fileBuild.fileBuildOption",
			TerminalFileBuildOptionTypes.Other.name,
		);
		if (gatewayName?.name === "Payeezy") {
			setValue("gateway.gatewayName", "");
		}
	};

	return (
		<div className={styles.section} data-autotest="programSection">
			{!isHiddenForApplication && (
				<Row>
					<Col md={5}>
						<HookSelectFormGroup
							label="Choose Program"
							options={programTypes}
							getOptionLabel={option => option.displayName}
							getOptionValue={option => option.value}
							placeholder="Please Select"
							name="terminalProgramType"
							isSearchable={false}
							cacheOptions
							defaultOptions
							debounceTimeout={DebounceTimeout}
							blurLoading={isLoading}
							showRequiredMark
							rules={{
								validate: {
									required,
								},
							}}
						/>
					</Col>
					<Col md={{ span: 5, offset: 2 }}>
						<HookFormInputGroup
							name="tidVNumber"
							register={register}
							label="Terminal ID"
							colInput={6}
							colLabel={6}
							blurLoading={isLoading}
							autoComplete="off"
							validator={{ maxLength50, required }}
							normalize={alphaNumericNormalizer}
							normalizeOnBlur={trimNormalizer}
							errors={errors}
							readOnly={isTidVNumberFromApi}
							warningText={
								isTidVNumberFromApi
									? CreatedViaTsysApiWarning
									: ""
							}
							showRequiredMark
						/>
					</Col>
				</Row>
			)}

			<div className="inlineItemsHeader">Back End Platform</div>
			<div className="inlineItems">
				<HookFormRadioButton
					id={`${sectionName}backendPlatformOmaha`}
					register={register}
					name={`${sectionName}backEndPlatform`}
					value={BackEndPlatformTypes.Omaha.name}
					label="Omaha"
					blurLoading={isLoading}
					disabled={!isBackEndPlatformOmaha}
					className={`item ${styles.bpItem}`}
				/>
				<HookFormRadioButton
					id={`${sectionName}backendPlatformNorth`}
					register={register}
					name={`${sectionName}backEndPlatform`}
					value={BackEndPlatformTypes.North.name}
					label="North"
					blurLoading={isLoading}
					blurMarginLeft="70px"
					disabled={!isBackEndPlatformNorth}
					className={`item ${styles.bpItem}`}
				/>
				<HookFormRadioButton
					id={`${sectionName}backendPlatformTsys`}
					register={register}
					name={`${sectionName}backEndPlatform`}
					value={BackEndPlatformTypes.Tsys.name}
					label="TSYS"
					blurLoading={isLoading}
					blurMarginLeft="70px"
					disabled={!isBackEndPlatformTsys}
					className={`item ${styles.bpItem}`}
				/>
				<HookFormRadioButton
					id={`${sectionName}backendPlatformStraddle`}
					register={register}
					name={`${sectionName}backEndPlatform`}
					value={BackEndPlatformTypes.Straddle.name}
					label="Straddle"
					blurLoading={isLoading}
					blurMarginLeft="70px"
					disabled={!achProcessor || isFileBuildOnly}
					className={`item ${styles.bpItem}`}
					onChange={onBackEndStraddlePlatformChange}
				/>
				<HookFormRadioButton
					id={`${sectionName}backendPlatformOther`}
					register={register}
					name={`${sectionName}backEndPlatform`}
					value={BackEndPlatformTypes.Other.name}
					label="Other"
					blurLoading={isLoading}
					blurMarginLeft="70px"
					disabled={!isBackEndPlatformOther}
					className={`item ${styles.bpItem}`}
				/>
				{!isLoading && (
					<HookFormInputGroup
						name={`${sectionName}otherBackEndPlatformName`}
						register={register}
						label="Name"
						colLabel={3}
						colInput={9}
						blurLoading={isLoading}
						autoComplete="off"
						className={`item ${styles.mbName}`}
						validator={
							isWatchedBackEndPlatformOther ? { required } : {}
						}
						showRequiredMark={isWatchedBackEndPlatformOther}
						disabled={!isWatchedBackEndPlatformOther}
					/>
				)}
			</div>
			{displayFrontEndPlatform && (
				<div className="mt-4">
					<p>Front End Platform</p>
					<Row>
						<Col md={2}>
							<HookFormCheckbox
								register={register}
								id={`${sectionName}omahaFrontEndPlatform`}
								name={`${sectionName}omahaFrontEndPlatform`}
								label={FrontEndPlatformTypes.Omaha.displayName}
								blurLoading={isLoading}
								className="mb-3"
								blurWidth="100%"
							/>
						</Col>
						<Col md={2}>
							<HookFormCheckbox
								register={register}
								id={`${sectionName}northFrontEndPlatform`}
								name={`${sectionName}northFrontEndPlatform`}
								label={FrontEndPlatformTypes.North.displayName}
								blurLoading={isLoading}
								className="mb-3"
								blurWidth="100%"
							/>
						</Col>
						<Col md={2}>
							<HookFormCheckbox
								register={register}
								id={`${sectionName}nashvilleFrontEndPlatform`}
								name={`${sectionName}nashvilleFrontEndPlatform`}
								label={
									FrontEndPlatformTypes.Nashville.displayName
								}
								blurLoading={isLoading}
								className="mb-3"
								blurWidth="100%"
							/>
						</Col>
					</Row>
					<Row className="form-group align-items-center">
						<Col md={2}>
							<HookFormCheckbox
								register={register}
								id={`${sectionName}compassFrontEndPlatform`}
								name={`${sectionName}compassFrontEndPlatform`}
								label={
									FrontEndPlatformTypes.Compass.displayName
								}
								blurLoading={isLoading}
								className="mb-3"
								blurWidth="100%"
							/>
						</Col>
						<Col md={2}>
							<HookFormCheckbox
								register={register}
								id={`${sectionName}buypassFrontEndPlatform`}
								name={`${sectionName}buypassFrontEndPlatform`}
								label={
									FrontEndPlatformTypes.BuyPass.displayName
								}
								blurLoading={isLoading}
								className="mb-3"
								blurWidth="100%"
							/>
						</Col>
						<Col md={2}>
							<HookFormCheckbox
								register={register}
								id={`${sectionName}otherFrontEndPlatform`}
								name={`${sectionName}otherFrontEndPlatform`}
								label={FrontEndPlatformTypes.Other.displayName}
								blurLoading={isLoading}
								className="mb-3"
								blurWidth="100%"
							/>
						</Col>
						<Col md={4}>
							<HookFormInputGroup
								name={`${sectionName}otherFrontEndPlatformName`}
								register={register}
								label="Name"
								colLabel={3}
								colInput={9}
								blurLoading={isLoading}
								autoComplete="off"
								disabled={!otherFrontEndPlatform}
								className={styles.mb18}
								validator={
									otherFrontEndPlatform && { required }
								}
								showRequiredMark={otherFrontEndPlatform}
							/>
						</Col>
					</Row>
					{!isHiddenForApplication && (
						<Row>
							<Col md={5}>
								<HookFormInputGroup
									name="frontEndMid"
									register={register}
									label="Front End MID"
									colInput={6}
									colLabel={6}
									blurLoading={isLoading}
									autoComplete="off"
									validator={{ maxLength50 }}
									normalize={alphaNumericNormalizer}
									normalizeOnBlur={trimNormalizer}
									errors={errors}
								/>
							</Col>
						</Row>
					)}
				</div>
			)}
		</div>
	);
};

export default Program;
